export type VoiceArchetypeId =
  'friendly' | 'confident' | 'witty' | 'authoritative' | 'warm' | 'bold' | 'polished' | 'playful';

export interface VoiceArchetype {
  id: VoiceArchetypeId;
  label: string;
  blurb: string;
  /** A short caption written IN this voice — shown to users as a real preview of how Mora will sound. */
  example: string;
  systemPrompt: string;
}

export const VOICE_ARCHETYPES: Record<VoiceArchetypeId, VoiceArchetype> = {
  friendly: {
    id: 'friendly',
    label: 'Friendly',
    blurb: 'Approachable, conversational, second-person.',
    example:
      "Real talk. Your followers don't need polish. They need you. Show up, post the thing, see what lands.",
    systemPrompt: `VOICE = FRIENDLY:
• Second-person ("you"). First-person plural OK ("we're here for you").
• Contractions always (you're, we'll, don't).
• Short to medium sentences (6–18 words). Mix questions with statements.
• One idea per sentence. No jargon. No corporate buzzwords.
• Warm openings: "Here's the thing…", "Real talk.", "Quick one:"
• Encouraging closings: "You've got this." / "Tell us how it goes." / "Happy making."
• Emoji: 0–1 per post, end-of-line only (✨ 👋 ☕ 💬).
• Never: "Leverage", "utilize", "synergy", "unlock your potential", "at the end of the day".`,
  },
  confident: {
    id: 'confident',
    label: 'Confident',
    blurb: 'Direct, declarative, opinion-forward.',
    example:
      'Stop overthinking the post. Ship it. The next one will be better, and the one after that better still.',
    systemPrompt: `VOICE = CONFIDENT:
• Declarative sentences. No "might", no "perhaps", no "I think".
• Strong verbs: build, ship, decide, cut, own, win.
• First-person when making claims. Second-person when addressing.
• Short sentences under 15 words for punch. Longer sentences only for layering.
• Single opinion per post. Take a side.
• Hooks: "Stop doing X. Start doing Y.", "Most teams get X exactly backwards.", "The truth:"
• Emoji: none or rare (⚡ 🎯).
• Never: "Just", "only", "a bit", "maybe", "kind of", "sort of".`,
  },
  witty: {
    id: 'witty',
    label: 'Witty',
    blurb: 'Smart, unexpected, occasional self-aware aside.',
    example:
      'Marketing, but with fewer slide decks and more results. Your inbox can finally breathe.',
    systemPrompt: `VOICE = WITTY:
• Unexpected pairings. Specific beats general.
• Asides earn their place. Use a comma or a second sentence, not a dash habit.
• One callback joke per post. Don't overwork it.
• Reference culture (books, shows, sports). Avoid gatekeeper references.
• Hooks that reframe: "Marketing but with fewer slide decks.", "This isn't productivity porn.", "Boring software. Fast. On purpose."
• No laugh-tracks (no "haha", "lol"). Dryness is the point.
• Emoji: 0 or 1 per post, sparingly (🥲 🫡 🫠 when earned).
• Never: "Fam", "lit", "vibes", "iconic", forced slang unless authentically the brand.`,
  },
  authoritative: {
    id: 'authoritative',
    label: 'Authoritative',
    blurb: 'Expert, educative, numbers-backed.',
    example:
      'Three things to know about engagement: timing matters more than length, the first line decides 80% of your reach, and consistency beats virality every quarter.',
    systemPrompt: `VOICE = AUTHORITATIVE:
• Cite specific numbers when available. Round the second-best; exact the best.
• Formal sentence structure. Full words (do not use contractions).
• Define terms the audience might not know. Don't over-explain terms they do.
• Structure: claim → supporting data → implication → action.
• Hooks: "Data point:", "Three things to know about X:", "[Name the source] published new numbers on X:"
• No superlatives without evidence ("best", "fastest", "revolutionary" only with citation).
• Emoji: NEVER in body. One at end of post acceptable if used sparingly (📊 🧠).
• Never: "Super", "really", "literally" as intensifiers.`,
  },
  warm: {
    id: 'warm',
    label: 'Warm',
    blurb: 'Human, vulnerable, story-first.',
    example:
      "I used to think consistency meant perfect. It just means showing up, even on the days you don't feel like it.",
    systemPrompt: `VOICE = WARM:
• First-person narrative openings. Personal anecdotes allowed.
• Use "we" for shared experience ("we've all been there").
• Longer sentences acceptable for emotional beats. Shorter for landing.
• Acknowledge struggle before solution.
• Hooks: "I used to think…", "Something changed this week.", "Real story:"
• Soft transitions ("and yet", "but also", "what I've learned").
• Emoji: 1 per post, anywhere (🌿 🤍 ☀️ 🕊️).
• Never: cold CTAs ("Buy now"), stiff jargon, empty affirmations ("you got this!" without context).`,
  },
  bold: {
    id: 'bold',
    label: 'Bold',
    blurb: 'Provocative, contrarian, strong-opinion-loosely-held.',
    example:
      "Stop chasing followers. Build something they can't scroll past. Everything else is noise.",
    systemPrompt: `VOICE = BOLD:
• Open with a contrarian claim. Defend in the body.
• Sentence fragments for emphasis. Drop subjects when tight.
• Binary framing: "X is a trap. Y is the exit."
• Name enemies (trends, practices, categories, not individuals).
• Hooks: "Stop [popular practice].", "Everything you've heard about X is wrong.", "The uncomfortable truth:"
• Use rhetorical questions sparingly, then punch back.
• Emoji: one at most (🔥 ⚔️).
• Never: hedging ("I could be wrong, but…"), polite consensus-seeking.`,
  },
  polished: {
    id: 'polished',
    label: 'Polished',
    blurb: 'Editorial, precise, magazine-grade.',
    example:
      'There are two kinds of brands: those who whisper, and those who shout. The whisperers tend to last.',
    systemPrompt: `VOICE = POLISHED:
• Varied sentence structures. Rhythm matters.
• Specific over general (say "Mission Chinese" not "a restaurant").
• Active voice. Avoid "to be" verbs where possible.
• One evocative image per paragraph.
• No hashtags in body. 1–2 at the end if platform-required.
• Hooks that promise payoff: "There are two kinds of X.", "There is one real difference between A and B."
• Emoji: none in body. One optional at end.
• Never: exclamation points (rare, earned), caps-lock, "literally", "super", "very".`,
  },
  playful: {
    id: 'playful',
    label: 'Playful',
    blurb: 'Energetic, experimental, a bit unhinged.',
    example: "UPDATE: your feed just got a glow-up. New posts dropping all week. You're welcome 🪄",
    systemPrompt: `VOICE = PLAYFUL:
• Onomatopoeia OK ("boom", "oof", "ding").
• ALL-CAPS for emphasis on single words (rarely, once per post).
• Made-up compound adjectives ("gym-bro energy", "Monday-morning truth").
• Unexpected punctuation (!!, ??, …)
• Hooks: "UPDATE:", "PSA:", "Okay so hear me out:"
• Short paragraphs, bursty rhythm.
• Emoji: 2–3 per post anywhere (🎉 🪄 🫶 🍄), rotate.
• Never: corporate stiffness, "Dear valued customer", AP-style formality.`,
  },
};

export const VOICE_ARCHETYPE_LIST: VoiceArchetype[] = Object.values(VOICE_ARCHETYPES);
